import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table, ul } from "../blocks.js";
import { QUOTED_SETS } from "../thresholds.js";
import type { WikiTopic } from "../types.js";

const ACTIONABLE = QUOTED_SETS.RANK_ACTIONABLE_DECILES;

export const rank: WikiTopic = {
  slug: "factor-rank-crossings",
  title: "Factor rank crossings",
  summary: "A symbol entering or leaving a factor's extreme decile at the session close.",
  group: "strategies",
  horizon: "position",
  strategyKey: "rank",
  sections: [
    {
      heading: "What it looks for",
      code: "rank_decile_entered / rank_decile_left",
      blocks: [
        p(
          <>
            Each symbol's factor percentile for today, held against the same symbol's percentile for
            the previous session. Both sides become deciles, and a row fires when the decile changed
            and one end of the move is an actionable decile.
          </>,
        ),
        p(
          <>
            The actionable deciles are <Datum>{ACTIONABLE.join(" and ")}</Datum>: the bottom and the
            top of the cross-section. A move between two middle deciles changes a number and nothing
            else, so it produces no row. Firing on all nine boundaries is nine times the volume for
            the same information.
          </>,
        ),
        p(
          <>
            Which of the two types you get depends on where the move landed. A move into an
            actionable decile is <Datum>rank_decile_entered</Datum>; a move out of one, to somewhere
            in the middle, is <Datum>rank_decile_left</Datum>.
          </>,
        ),
      ],
    },
    {
      heading: "The three gates",
      blocks: [
        p(
          <>All three exist to keep the emitter quiet on the days it would otherwise be loudest.</>,
        ),
        ul([
          <>
            An empty previous session returns nothing at all. The day after a backfill, every
            symbol's decile "changed" from nothing to something, which would have put{" "}
            <Datum>2,858</Datum> rows into a Telegram chat on day one. That is how someone mutes the
            bot permanently.
          </>,
          <>
            A symbol absent from the previous session is skipped. A new listing has no decile to
            cross from, so its first score is a starting point rather than a crossing.
          </>,
          <>
            An unknown factor id is skipped. Only factors in the live registry produce rows, so
            retiring a factor stops its signals instead of stranding them.
          </>,
        ]),
        note(
          <>
            The gates run per symbol and per factor. One symbol missing from yesterday costs that
            symbol's rows, not the session's.
          </>,
        ),
      ],
    },
    {
      heading: "Why it stays at info",
      blocks: [
        p(
          <>
            Severity is hardcoded to <Datum>info</Datum>. No threshold table, no magnitude
            comparison, no way to earn a louder level. Nobody has measured what a decile crossing is
            worth, and an invented threshold produces a plausible alert, which is worse than no
            alert.
          </>,
        ),
        p(
          <>
            It is also the loudest emitter in the system, at roughly <Datum>1,600</Datum> rows a
            session across the universe, because every name that moves across a decile boundary
            produces one. At <Datum>warn</Datum> it cleared the default alert floor, so a subscriber
            got <Datum>1,600</Datum> messages a day and the web feed became a wall of orange with
            the measured strategies buried underneath.
          </>,
        ),
        note(
          <>
            Info keeps every row in the feed and puts it below the notification floor. A reader who
            wants them back adds one alert rule with a minimum severity of <Datum>info</Datum>, and
            nothing in the emitter changes.
          </>,
        ),
      ],
    },
    {
      heading: "What the row carries",
      blocks: [
        table(
          "Rank crossing payload",
          ["Field", "What it holds"],
          [
            ["factorId", "The factor that moved, by registry id"],
            ["from", "Yesterday's percentile, 0 to 1"],
            ["to", "Today's percentile, 0 to 1"],
            ["decile", <Datum key="d">{ACTIONABLE.join(" or ")}</Datum>],
          ],
        ),
        p(
          <>
            The decile field carries the actionable end of the move: the decile entered on an{" "}
            <Datum>entered</Datum> row, and the decile left on a <Datum>left</Datum> row. The two
            percentiles are the raw cross-sectional ranks, not the deciles, so the size of the move
            inside the decile survives.
          </>,
        ),
        p(
          <>
            The row is stamped at the <Datum>15:30</Datum> IST close, though the job runs at{" "}
            <Datum>20:15</Datum> once the day's factor scoring has finished. Occurrence time is the
            market event, not the compute time.
          </>,
        ),
      ],
    },
    {
      heading: "The trap",
      blocks: [
        p(
          <>
            A percentile is not direction-corrected. A high rank on 60-day volatility, net debt, or
            accruals is bad, not good, so entering decile <Datum>{Math.max(...ACTIONABLE)}</Datum>{" "}
            on one of those is a warning rather than a buy. Only the composite flips direction.
          </>,
        ),
        p(
          <>
            Read the factors, percentiles, and deciles topic before acting on a single-factor
            crossing. It covers which factors invert and how the decile is computed from the
            percentile.
          </>,
        ),
      ],
    },
  ],
};
